// Device-pixel-ratio-aware canvas sizing.
//
// A canvas with width/height set in CSS pixels but no backing-store scale
// renders blurry on every retina/HiDPI display (DPR ≥ 1.5 — most modern
// laptops, every iOS device, most Android phones). The fix:
//
//   1. Set the CSS size in CSS pixels (canvas.style.width/height).
//   2. Set the canvas's actual buffer to cssSize × dpr.
//   3. ctx.scale(dpr, dpr) so all drawing happens in CSS-pixel coordinates
//      but renders at native resolution.
//
// `resizeCanvasForDpr` is idempotent — call it on mount, on ResizeObserver
// fires, and on DPR change (e.g. dragging the window across monitors with
// different scaling).

export function resizeCanvasForDpr(canvas, ctx, width, height) {
  if (!canvas || !ctx) return;
  const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  // Set CSS size so the element occupies the intended layout space.
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  // Backing-store buffer in physical pixels.
  canvas.width = Math.max(1, Math.round(width * dpr));
  canvas.height = Math.max(1, Math.round(height * dpr));
  // Reset prior transforms before re-scaling — otherwise repeated resizes
  // compound the scale and the drawing shrinks to nothing.
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
}

/**
 * Observe a canvas + invoke `redraw(width, height)` on mount, on size
 * changes, and on DPR changes. Returns a cleanup function.
 *
 * The redraw callback receives CSS-pixel dimensions — it should not worry
 * about DPR; resizeCanvasForDpr has already configured the context.
 */
export function observeCanvas(canvas, redraw) {
  if (!canvas) return () => {};
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};

  let lastDpr = window.devicePixelRatio || 1;
  let mql = null;

  const run = () => {
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    resizeCanvasForDpr(canvas, ctx, w, h);
    redraw(ctx, w, h);
  };

  const ro = new ResizeObserver(run);
  ro.observe(canvas);

  // Listen for DPR changes (window dragged across monitors with different
  // scaling). matchMedia returns a fresh MQL whenever the resolution
  // crosses the threshold, so we re-bind each time.
  const watchDpr = () => {
    if (mql) mql.removeEventListener("change", onDprChange);
    lastDpr = window.devicePixelRatio || 1;
    mql = window.matchMedia(`(resolution: ${lastDpr}dppx)`);
    mql.addEventListener("change", onDprChange);
  };
  const onDprChange = () => {
    watchDpr();
    run();
  };
  watchDpr();

  // First draw.
  run();

  return () => {
    ro.disconnect();
    if (mql) mql.removeEventListener("change", onDprChange);
  };
}
